/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useFirebase } from 'react-redux-firebase';
import html2canvas from 'html2canvas';
import { Alert } from '@material-ui/lab';
import { Snackbar } from '@material-ui/core';
import {
  selectCurrentHit,
  selectCurrentHitCategory,
  selectCurrentHitIndex,
  selectClientId,
  selectCheck,
  selectCount,
  selectApplicationImages,
  selectInput,
  selectDebug,
  setCurrentHitCategory,
  getQueryLog,
  setCurrentHitIndex,
  setDebug,
  setZoomSelfie,
  setZoomIdFace,
  setZoomIdDetails,
  setScreenShotDone,
  setScreenShotError,
  selectError,
  selectFetchStatus,
  selectZoomIdDetails,
  selectZoomIdFace,
  selectZoomSelfie,
  selectScreenShotDone,
  selectErrorMessage,
  selectNoHit,
} from './reviewSlice';
import ApplicationBar from '../../components/applicationBar/index';
import MismatchBar from '../../components/mismatchBar/index';
import SelfieComparison from '../../components/selfieComparison/index';
import IdFaceComparison from '../../components/idFaceComparison/index';
import IdDetailsComparison from '../../components/idDetailsComparison/index';
import Spinner from '../../components/spinner/index';
import SearchBar from '../../components/searchBar/index';
import './reviewPage.css';
import { signOut } from '../loginPage/loginSlice';

const ReviewPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const firebaseAuth = useSelector((state: any) => state.firebase.auth);
  const firebase = useFirebase();
  const { requestId } = useParams<{ requestId: string }>();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();
  const category = query.get('category');
  const hitNumber = query.get('hitNumber');
  const debug = query.get('debug');

  useEffect(() => {
    const getToken = async (): Promise<string> => {
      if (!firebaseAuth.uid) return '';
      try {
        const token = await firebase.auth().currentUser?.getIdToken(true);
        return token || '';
      } catch (err) {
        return '';
      }
    };
    dispatch(getQueryLog(requestId, getToken));
  }, [dispatch, requestId, firebaseAuth, firebase]);

  useEffect(() => {
    dispatch(
      setCurrentHitCategory(category === 'matches' ? 'matches' : 'mismatches')
    );
  }, [dispatch, category]);

  useEffect(() => {
    dispatch(setCurrentHitIndex(hitNumber ? Number(hitNumber) - 1 : 0));
  }, [dispatch, hitNumber]);

  useEffect(() => {
    dispatch(setDebug(debug));
  }, [dispatch, debug]);

  const [prev, setPrev] = useState(false);
  const [next, setNext] = useState(false);

  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') setPrev(true);
      if (e.key === 'ArrowRight') setNext(true);
    });
  }, []);

  const currentHit = useSelector(selectCurrentHit);
  const currentHitCategory = useSelector(selectCurrentHitCategory);
  const currentHitIndex = useSelector(selectCurrentHitIndex);
  const clientId = useSelector(selectClientId);
  const checkData = useSelector(selectCheck);
  const countData = useSelector(selectCount);
  const applicationImages = useSelector(selectApplicationImages);
  const input = useSelector(selectInput);
  const debugValue = useSelector(selectDebug);
  const fetchFlag = useSelector(selectFetchStatus);
  const errorStatus = useSelector(selectError);
  const zoomSelfie = useSelector(selectZoomSelfie);
  const zoomIdFace = useSelector(selectZoomIdFace);
  const zoomIdDetails = useSelector(selectZoomIdDetails);
  const screenShotDone = useSelector(selectScreenShotDone);
  const errorMessage = useSelector(selectErrorMessage);
  const noHit = useSelector(selectNoHit);

  const nextHitHandler = (scrollFlag: boolean) => {
    const totalHit = countData.total[currentHitCategory];
    let hitNumberValue;
    if (totalHit) {
      if (currentHitIndex + 1 >= totalHit) {
        hitNumberValue = 0;
      } else {
        hitNumberValue = currentHitIndex + 1;
      }
      hitNumberValue += 1;
    }
    history.push({
      pathname: `/review/${requestId}`,
      search: `?category=${currentHitCategory}&hitNumber=${hitNumberValue}&debug=${debugValue}`,
    });
    if (scrollFlag) window.scrollTo(0, 0);
  };

  const previousHitHandler = (scrollFlag: boolean) => {
    const totalHit = countData.total[currentHitCategory];
    let hitNumberValue;
    if (totalHit) {
      if (currentHitIndex - 1 < 0) {
        hitNumberValue = totalHit - 1;
      } else {
        hitNumberValue = currentHitIndex - 1;
      }
      hitNumberValue += 1;
    }
    history.push({
      pathname: `/review/${requestId}`,
      search: `?category=${currentHitCategory}&hitNumber=${hitNumberValue}&debug=${debugValue}`,
    });
    if (scrollFlag) window.scrollTo(0, 0);
  };

  if (prev) {
    setPrev(false);
    previousHitHandler(false);
  }

  if (next) {
    setNext(false);
    nextHitHandler(false);
  }

  const setHitCategoryHandler = (hitCategory: 'mismatches' | 'matches') => {
    history.push({
      pathname: `/review/${requestId}`,
      search: `?category=${hitCategory}&hitNumber=1&debug=${debugValue}`,
    });
  };

  const setDebugHandler = () => {
    const debugData = !debugValue;
    history.push({
      pathname: `/review/${requestId}`,
      search: `?category=${currentHitCategory}&hitNumber=${
        currentHitIndex + 1
      }&debug=${debugData}`,
    });
  };

  const zoomSelfieHandler = () => {
    dispatch(setZoomSelfie());
  };

  const zoomIdFaceHandler = () => {
    dispatch(setZoomIdFace());
  };

  const zoomIdDetailsHandler = () => {
    dispatch(setZoomIdDetails());
  };

  // Don't remove ts-ignore - Clipboard API is not supported by typescript for now - 2021
  /* eslint-disable  @typescript-eslint/no-unused-vars */
  const screenshotHandler = () => {
    try {
      html2canvas(document.body, {
        proxy: `${process.env.REACT_APP_BACKEND_URL}/v1/proxy`,
      }).then((canvas) => {
        canvas.toBlob(async (blob) => {
          if (blob) {
            const blobData = [
              /*
                // @ts-ignore */
              new ClipboardItem({
                [blob.type]: blob,
              }),
            ];
            /*
              // @ts-ignore */
            await navigator.clipboard.write(blobData);

            dispatch(setScreenShotError(false));
            dispatch(setScreenShotDone(true));
          }
        });
      });
    } finally {
      dispatch(setScreenShotDone(false));
      dispatch(setScreenShotError(true));
    }
  };

  const screenshotDoneHandler = () => {
    dispatch(setScreenShotDone(false));
  };

  const logOutHandler = () => {
    dispatch(signOut());
  };

  // Make sure to update AI decision based on interface change
  // Make sure to add threshold here. Right now its optional
  const selfieDetails = {
    matchScore: currentHit?.matchScore?.selfie,
    distance: currentHit?.distances?.selfie,
    AIDecision: currentHit?.aiDecision?.selfie
      ? currentHit.aiDecision.selfie
      : 'NA',
  };
  // Make sure to update AI decision based on interface change
  // Make sure to add threshold here. Right now its optional
  const faceDetails = {
    matchScore: currentHit?.matchScore?.idFaceCrop,
    distance: currentHit?.distances?.idFaceCrop,
    AIDecision: currentHit?.aiDecision?.idFace
      ? currentHit.aiDecision.idFace
      : 'NA',
  };
  const facePPData = {
    selfieBlur: checkData?.selfie?.quality?.blur?.overall,
    faceQuality: checkData?.selfie?.quality?.faceQuality?.value,
    threshold: checkData?.selfie?.quality?.faceQuality?.threshold,
    isImageRotated: checkData?.selfie?.rotation?.rotated,
    rotatedAngle: checkData?.selfie?.rotation?.angle,
    facePPAngle: checkData?.selfie?.rotation?.facePPAngle,
  };
  const inputData = {
    ocrNid: input?.ocr?.id?.value ? input.ocr.id.value : 'NA',
    ocrNidConf: input?.ocr?.id?.conf ? input?.ocr?.id?.conf : 'NA',
    inputNid: input?.user?.idNumber ? input.user.idNumber : 'NA',
    nidMatch: checkData?.matchingAPI?.idNumber,
    ocrName: input?.ocr?.name?.value ? input.ocr.name.value : 'NA',
    ocrNameConf: input?.ocr?.name?.conf ? input?.ocr?.name?.conf : 'NA',
    inputName: input?.user?.name ? input.user.name : 'NA',
    nameMatch: checkData?.matchingAPI?.name,
    ocrDob: input?.ocr?.dob?.value ? input.ocr.dob.value : 'NA',
    ocrDobConf: input?.ocr?.dob?.conf ? input?.ocr?.dob?.conf : 'NA',
    inputDob: input?.user?.dob ? input.user.dob : 'NA',
    dobMatch: checkData?.matchingAPI?.dob,
  };
  const idDetails = {
    appNid: input?.query?.idNumber,
    dbNid: currentHit.nid,
    nidMatch: currentHit?.aiDecision?.idNumber
      ? currentHit.aiDecision.idNumber
      : 'NA',
    nameMatch: currentHit?.aiDecision?.name ? currentHit.aiDecision.name : 'NA',
    dobMatch: currentHit?.aiDecision?.dob ? currentHit.aiDecision.dob : 'NA',
    appName: input?.query?.name,
    dbName: currentHit.name,
    appDob: input?.query?.dob,
    dbDob: currentHit.dob,
  };

  const reviewRender = (
    <div className="ReviewPage">
      <SearchBar
        debug={setDebugHandler}
        debugValue={debugValue}
        logOut={logOutHandler}
      />
      <ApplicationBar
        requestId={requestId}
        clientId={clientId}
        verifyStatus={checkData?.verify?.value}
        enrolment={checkData?.enrol?.value}
        selfieBlur={checkData?.selfie?.quality?.blur?.overall}
        verifyPair={checkData?.verifyPair?.match}
        applicationId={input?.user?.applicationId}
      />
      {noHit ? (
        <Alert severity="info">No match/mismatch available</Alert>
      ) : null}
      <MismatchBar
        decision={currentHit.reason}
        debug={debugValue}
        appId={currentHit.appId}
        categoryAlias={currentHit.categoryAlias}
        categoryId={currentHit.categoryId}
        enrollDate={currentHit.enrollDate}
        clientId={currentHit.clientId}
        requestId={currentHit.requestId}
        noOfMatch={countData.total.matches}
        noOfMismatch={countData.total.mismatches}
        active={currentHitCategory}
        currentActive={currentHitIndex + 1}
        exist={currentHit.exist}
        latestEnrollment={currentHit.latestEnrollment}
        conf={currentHit.conf}
        personId={currentHit.personId}
        otherAppIds={currentHit.otherAppIds}
        blacklisted={currentHit.blacklisted}
        nextHit={nextHitHandler}
        previousHit={previousHitHandler}
        setHitCategory={setHitCategoryHandler}
      />
      <div className="ReviewPage-Flex-Container">
        <div className="ReviewPage-Selfie">
          <SelfieComparison
            originalAppImage={applicationImages?.selfieImages?.original}
            originalDbImage={currentHit?.selfieImages?.original}
            cropAppImage={applicationImages?.selfieImages?.fraasCrop}
            cropDbImage={currentHit?.selfieImages?.fraasCrop}
            selfieDetails={selfieDetails}
            facePPDetails={facePPData}
            zoom={zoomSelfie}
            debug={debugValue}
            zoomSelfieHandler={zoomSelfieHandler}
          />
        </div>
        <div className="ReviewPage-IdFace">
          <IdFaceComparison
            originalAppIdImage={applicationImages?.idImages?.original}
            originalDbIdImage={currentHit?.idImages?.original}
            cropFaceAppImage={applicationImages?.idImages?.fraasCrop}
            cropFaceDbImage={currentHit?.idImages?.fraasCrop}
            faceDetails={faceDetails}
            zoom={zoomIdFace}
            debug={debugValue}
            zoomIdFaceHandler={zoomIdFaceHandler}
          />
        </div>
      </div>
      <div className="ReviewPage-Bottom-Container">
        <IdDetailsComparison
          cropAppIdImage={applicationImages?.idImages?.ssd}
          cropDbIdImage={currentHit?.idImages?.ssd}
          idDetails={idDetails}
          ocrDetails={inputData}
          zoom={zoomIdDetails}
          zoomIdDetailsHandler={zoomIdDetailsHandler}
        />
      </div>

      <Snackbar
        open={screenShotDone}
        autoHideDuration={4000}
        onClose={screenshotDoneHandler}
      >
        <Alert onClose={screenshotDoneHandler} severity="success">
          Screenshot copied to clipboard
        </Alert>
      </Snackbar>
    </div>
  );

  let loadingRender = <Spinner />;
  if (errorStatus)
    loadingRender = (
      <div>
        <Alert severity="error">{errorMessage}</Alert>
      </div>
    );
  const actualRender = fetchFlag ? reviewRender : loadingRender;

  return actualRender;
};

export default ReviewPage;
